import React from 'react';
import { motion } from 'motion/react';
import { PlayCircle, FileText, Landmark, Lightbulb, ClipboardList, PenTool, ArrowLeft } from 'lucide-react';
import { useTranslate } from '../context/LanguageContext';
import { cn, getImageUrl } from '../lib/utils';
import { useData } from '../context/DataContext';

const CATEGORIES = [
  {
    id: 1,
    title: 'الفيديوهات',
    description: 'المهنية هي التزام بالكفاءة والاحترام، حيث يجتمع الإتقان مع الأخلاق لبناء ثقة تدوم وتجقق الإنجاز',
    icon: <PlayCircle className="w-5 h-5 text-white" />,
    image: 'https://images.unsplash.com/photo-1492691523567-6119e289dfdf?q=80&w=200&h=200&auto=format&fit=crop',
    bgColor: 'bg-indigo-500/20',
    href: '/videos'
  },
  {
    id: 2,
    title: 'المقالات',
    description: 'عندما تعبر عن وجهة نظرك، تأكد من أن أسلوبك مهذب ومنفتح على الحوار، مما يعزز فرص الإقناع.',
    icon: <FileText className="w-5 h-5 text-white" />,
    image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?q=80&w=200&h=200&auto=format&fit=crop',
    bgColor: 'bg-emerald-500/20',
    href: '/articles'
  },
  {
    id: 3,
    title: 'الميدان الثورة',
    description: 'الثورة الحقيقية هي تلك التي تحرر العقول قبل الأوطان، فتغرس بذور الوعي والكرامة ليزهر المستقبل حرية وعدالة.',
    icon: <Landmark className="w-5 h-5 text-white" />,
    image: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?q=80&w=200&h=200&auto=format&fit=crop',
    bgColor: 'bg-amber-500/20',
    href: '/category/news'
  },
  {
    id: 4,
    title: 'القضايا الفكرية',
    description: 'الفكرة القوية ليست تلك التي تفرض نفسها، بل التي تدفع الآخرين للتفكير والتفاعل معها، فتتحول إلى حوار بناء بدلاً من فرض رأي.',
    icon: <Lightbulb className="w-5 h-5 text-white" />,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=200&h=200&auto=format&fit=crop',
    bgColor: 'bg-rose-500/20',
    href: '/category/intellectual'
  },
  {
    id: 5,
    title: 'حوارات',
    description: 'الحوار هو جسر التواصل بين العقول، به نتبادل الأفكار ونبني التفاهم المشترك الذي يثري المجتمع ويحقق التقارب.',
    icon: <PlayCircle className="w-5 h-5 text-white" />,
    image: 'https://images.unsplash.com/photo-1551818255-e6e10975bc17?q=80&w=200&h=200&auto=format&fit=crop',
    bgColor: 'bg-sky-500/20',
    href: '/category/dialogues'
  },
  {
    id: 6,
    title: 'الأقلام الحرة',
    description: 'الرأي الحر هو نبض الإنسانية، فهو يعبر عن الحق دون خوف، ويزرع في المجتمع شجاعة التفكير والتغيير.',
    icon: <PenTool className="w-5 h-5 text-white" />,
    image: 'https://images.unsplash.com/photo-1544077960-604201fe746a?q=80&w=200&h=200&auto=format&fit=crop',
    bgColor: 'bg-violet-500/20',
    href: '/category/free-pens'
  }
];

export default function FeaturedCategories() {
  const { language, t } = useTranslate();
  const { settings } = useData();
  const featuredImages = settings?.featured_category_images || {};
  return (
    <section className="bg-primary py-16 md:py-24 px-4 relative overflow-hidden">
      {/* Decorative patterns */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 blur-[150px] rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 blur-[150px] rounded-full translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3.5xl font-black text-white mb-4 tracking-tight">{t('أبرز الفئات المميزة')}</h2>
          <p className="text-zinc-200 text-[12px] font-normal not-italic max-w-2xl mx-auto leading-relaxed opacity-90">{t('استكشف كل ما هو حديث ومبتكر في عالم العمل السياسي والفكري.')}</p>
          <div className="mt-8 flex items-center justify-center gap-4">
             <div className="h-px w-20 bg-white/20" />
             <div className="w-16 h-1.5 bg-secondary shadow-[0_0_20px_rgba(255,63,52,0.4)]" />
             <div className="h-px w-20 bg-white/20" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {CATEGORIES.map((cat, i) => {
            const customImage = featuredImages[cat.id] || cat.image;
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -6, scale: 1.025 }}
                className="bg-white/[0.03] backdrop-blur-md p-8 lg:p-10 border border-white/10 hover:border-secondary/60 hover:bg-white/5 hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-300 text-center group rounded-3xl cursor-pointer"
              >
                <div className="relative mb-8 flex justify-center">
                  <div className="w-24 h-24 overflow-hidden rounded-2xl border-4 border-white/10 p-1 group-hover:border-secondary transition-colors duration-500 shadow-2xl">
                    <img 
                      src={getImageUrl(customImage)} 
                      alt={t(cat.title)} 
                      className="w-full h-full object-cover rounded-xl filter grayscale group-hover:grayscale-0 transition-all duration-700 scale-110 group-hover:scale-100" 
                    />
                  </div>
                  <div className="absolute -bottom-2 right-1/2 translate-x-4 w-10 h-10 rounded-xl bg-secondary flex items-center justify-center shadow-[0_4px_15px_rgba(255,63,52,0.4)] transform group-hover:scale-110 transition-transform duration-300">
                     {cat.icon}
                  </div>
                </div>

                <h3 className="text-[14px] font-normal text-white mb-4 group-hover:text-secondary transition-colors duration-300">
                  {t(cat.title)}
                </h3>
                
                <p className="text-zinc-100/80 text-[12px] leading-loose font-normal not-italic line-clamp-3 mb-8 px-2">
                  {t(cat.description)}
                </p>
                
                <div className="pt-8 border-t border-white/5">
                   <a 
                     href={cat.href}
                     className="inline-flex items-center gap-3 text-xs font-black text-secondary tracking-[0.1em] uppercase hover:text-white transition-all group/btn border-b-2 border-transparent hover:border-secondary pb-1 px-2"
                   >
                     <span>{t('عرض كامل التفاصيل')}</span>
                     <ArrowLeft size={16} className={cn("transform transition-transform", language === 'ar' ? "group-hover/btn:-translate-x-2" : "group-hover/btn:translate-x-2 rotate-180")} />
                   </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
